import React, { useState, useEffect, useRef } from 'react';
import ChatArea from './ChatArea';
import Sidebar from './Sidebar';
import CreditPanel from './CreditPanel';
import FilePanel from './FilePanel';
import { useAuth0 } from '@auth0/auth0-react';
import { authenticateUser, proofread, copyedit, getCredits } from '../api/otjApi';
import { getCurrentCredits, calculateCredits, canUseCredits, deductCredits } from '../utils/credits';
import MessageInput from './MessageInput';

const ChatApp = () => {
  const { user, isAuthenticated, isLoading } = useAuth0();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [currentView, setCurrentView] = useState('chat'); // 'chat' 또는 'file'
  const [currentChatId, setCurrentChatId] = useState(null);
  const [chats, setChats] = useState([]);
  const [otjUser, setOtjUser] = useState(null);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [guestCredits, setGuestCredits] = useState(0);
  const [isCreditPanelOpen, setIsCreditPanelOpen] = useState(false);
  
  // 기다리는 메시지 타이머 관리를 위한 refs
  const waitingMessageIntervalRef = useRef(null);
  const startTimeoutRef = useRef(null);

  // 메뉴 선택 핸들러
  const handleMenuSelect = (menuType) => {
    setCurrentView(menuType);
    setIsSidebarOpen(false);
  };

  const createNewChat = () => {
    const newChatId = Date.now().toString();
    const newChat = {
      id: newChatId,
      title: '새로운 대화',
      messages: [],
      createdAt: new Date().toISOString()
    };
    setChats(prev => [newChat, ...prev]);
    setCurrentChatId(newChatId);
    setIsSidebarOpen(false);
    return newChatId;
  };

  // 기본 채팅 생성 (한 번만 실행)
  useEffect(() => {
    if (chats.length === 0) {
      createNewChat();
    }
  }, [chats.length]);

  // 게스트 크레딧 초기화 및 주기적 업데이트
  useEffect(() => {
    const updateCredits = () => {
      const credits = getCurrentCredits();
      setGuestCredits(credits);
    };

    // 초기 크레딧 로드
    updateCredits();

    // 1분마다 크레딧 상태 확인 (하루 리셋 감지용)
    const interval = setInterval(updateCredits, 60000);

    return () => clearInterval(interval);
  }, []);

  // 컴포넌트 언마운트 시 기다리는 메시지 타이머 정리
  useEffect(() => {
    return () => {
      if (startTimeoutRef.current) {
        clearTimeout(startTimeoutRef.current);
      }
      if (waitingMessageIntervalRef.current) {
        clearInterval(waitingMessageIntervalRef.current);
      }
    };
  }, []);

  // 사용자 인증 처리
  useEffect(() => {
    if (isAuthenticated && user && !otjUser && !isAuthenticating) {
      handleUserAuthentication();
    }
  }, [isAuthenticated, user, otjUser, isAuthenticating]);

  const handleUserAuthentication = async () => {
    setIsAuthenticating(true);
    setAuthError(null);
    
    try {
      const result = await authenticateUser(user);
      
      if (result.success) {
        setOtjUser(result.user);
      } else {
        setAuthError(result.error);
      }
    } catch (error) {
      console.error('인증 오류:', error);
      setAuthError('사용자 인증 중 오류가 발생했습니다.');
    } finally {
      setIsAuthenticating(false);
    }
  };

  const selectChat = (chatId) => {
    setCurrentChatId(chatId);
    setIsSidebarOpen(false);
  };

  const deleteChat = (chatId) => {
    const filtered = chats.filter(c => c.id !== chatId);
    setChats(filtered);
    if (filtered.length > 0) {
      setCurrentChatId(filtered[0].id);
    } else {
      createNewChat();
    }
  };

  const updateChatTitle = (chatId, title) => {
    setChats(prev => prev.map(chat => 
      chat.id === chatId ? { ...chat, title } : chat
    ));
  };

  const addMessage = (chatId, message) => {
    console.log('[ChatApp] addMessage 호출:', { chatId, message });
    setChats(prev => {
      const updated = prev.map(chat => 
        chat.id === chatId 
          ? { ...chat, messages: [...chat.messages, message] }
          : chat
      );
      console.log('[ChatApp] 업데이트된 chats:', updated);
      return updated;
    });
  };

  const openCreditPanel = () => {
    setIsCreditPanelOpen(true);
  };

  const closeCreditPanel = () => {
    setIsCreditPanelOpen(false);
  };

  const handleCreditAdded = async (addedCredits) => {
    // 로그인한 사용자의 경우 서버에서 최신 크레딧 정보 가져오기
    if (isAuthenticated && otjUser?.primaryKey) {
      try {
        const creditResponse = await getCredits({ apikey: otjUser.primaryKey });
        if (creditResponse.status === 200 && creditResponse.data) {
          setOtjUser(prevUser => ({
            ...prevUser,
            remainingCredits: creditResponse.data.remainingCredits
          }));
        }
      } catch (error) {
        console.error('크레딧 갱신 실패:', error);
      }
    } else {
      // 게스트 사용자의 경우 로컬 크레딧 업데이트
      const newCredits = getCurrentCredits() + addedCredits;
      setGuestCredits(newCredits);
    }
    
    // 패널 닫기
    setIsCreditPanelOpen(false);
  };

  // 교정/교열 요청 처리
  const handleTextProcessing = async ({ text, mode, includereasons }) => {
    console.log('[ChatApp] handleTextProcessing 시작:', { text, mode, includereasons });
    
    // 게스트 사용자인 경우 크레딧 확인 (로그인한 사용자는 기존 크레딧 시스템 사용)
    if (!isAuthenticated) {
      const requiredCredits = calculateCredits(text, mode, includereasons);
      console.log('[ChatApp] 필요한 크레딧:', requiredCredits);
      
      if (!canUseCredits(requiredCredits)) {
        const creditShortageMessage = {
          id: Date.now(),
          type: 'credit_shortage',
          requiredCredits,
          currentCredits: guestCredits,
          timestamp: new Date().toISOString()
        };
        
        let chatId = currentChatId;
        if (!chatId) {
          chatId = createNewChat();
        }
        addMessage(chatId, creditShortageMessage);
        return;
      }
    }
    
    // 현재 채팅이 없으면 새로 생성
    let chatId = currentChatId;
    if (!chatId) {
      console.log('[ChatApp] 새 채팅 생성');
      chatId = createNewChat();
    }
    console.log('[ChatApp] 사용할 chatId:', chatId);

    // 로딩 메시지 추가 (사용자 메시지는 표시하지 않음)
    const loadingMessage = {
      id: Date.now(),
      type: 'loading',
      content: `${mode === 'proofread' ? '교정' : '교열'} 중입니다...`,
      timestamp: new Date().toISOString()
    };
    console.log('[ChatApp] 로딩 메시지 추가:', loadingMessage);
    addMessage(chatId, loadingMessage);

    // 5초마다 추가 메시지를 표시하는 타이머
    let waitingMessageCounter = 0;
    const waitingMessages = [
      '조금 더 기다려 주세요...',
      '처리 중입니다. 잠시만 기다려 주세요...',
      '거의 완료되었습니다...',
      '최상의 결과를 위해 정성스럽게 처리하고 있습니다...',
      '곧 완료됩니다. 조금만 더 기다려 주세요...'
    ];

    const startWaitingMessages = () => {
      waitingMessageIntervalRef.current = setInterval(() => {
        if (waitingMessageCounter < waitingMessages.length) {
          const waitingMessage = {
            id: Date.now(),
            type: 'waiting',
            content: waitingMessages[waitingMessageCounter],
            timestamp: new Date().toISOString()
          };
          addMessage(chatId, waitingMessage);
          waitingMessageCounter++;
        }
      }, 5000);
    };

    const stopWaitingMessages = () => {
      // 5초 후 시작하는 타이머 정리
      if (startTimeoutRef.current) {
        clearTimeout(startTimeoutRef.current);
        startTimeoutRef.current = null;
      }
      // 기다리는 메시지 interval 정리
      if (waitingMessageIntervalRef.current) {
        clearInterval(waitingMessageIntervalRef.current);
        waitingMessageIntervalRef.current = null;
      }
    };

    // 5초 후 추가 메시지 시작
    startTimeoutRef.current = setTimeout(() => {
      startWaitingMessages();
    }, 5000);

    try {
      let response;
      const userApiKey = otjUser && otjUser.primaryKey ? otjUser.primaryKey : undefined;
      console.log('[ChatApp] API 호출 시작:', { mode, userApiKey: !!userApiKey });
      console.log('[ChatApp] 호출할 함수:', mode === 'proofread' ? 'proofread' : 'copyedit');
      console.log('[ChatApp] 호출 파라미터:', { text, includereasons, language: 'kr', userApiKey });
      
      if (mode === 'proofread') {
        console.log('[ChatApp] proofread 함수 호출 직전');
        response = await proofread({
          text,
          includereasons,
          language: 'kr',
          userApiKey
        });
        console.log('[ChatApp] proofread 함수 호출 완료');
      } else if (mode === 'copyedit') {
        console.log('[ChatApp] copyedit 함수 호출 직전');
        response = await copyedit({
          text,
          includereasons,
          language: 'kr',
          userApiKey
        });
        console.log('[ChatApp] copyedit 함수 호출 완료');
      } else {
        // subedit 등 미구현 모드 처리
        console.log('[ChatApp] 미구현 모드:', mode);
        
        // 타이머 정리
        stopWaitingMessages();
        
        setChats(prev => prev.map(chat => 
          chat.id === chatId 
            ? { ...chat, messages: chat.messages.filter(msg => msg.type !== 'loading' && msg.type !== 'waiting') }
            : chat
        ));
        const errorMessage = {
          id: Date.now() + 1,
          type: 'error',
          content: '이 기능은 아직 지원되지 않습니다.',
          timestamp: new Date().toISOString()
        };
        addMessage(chatId, errorMessage);
        return;
      }

      console.log('[ChatApp] API 응답 받음:', { status: response?.status, data: response?.data });

      // 타이머 정리
      stopWaitingMessages();

      // 로딩 메시지와 대기 메시지 모두 제거
      console.log('[ChatApp] 로딩 및 대기 메시지 제거');
      setChats(prev => prev.map(chat => 
        chat.id === chatId 
          ? { ...chat, messages: chat.messages.filter(msg => msg.type !== 'loading' && msg.type !== 'waiting') }
          : chat
      ));

      if (response.status === 200) {
        const data = response.data;
        const isCachedResult = response.statusText === 'OK (Cached)';
        console.log('[ChatApp] API 응답 성공:', data);
        console.log('[ChatApp] 캐시된 결과:', isCachedResult);
        
        // 게스트 사용자인 경우 크레딧 차감 (캐시된 결과가 아닌 경우에만)
        if (!isAuthenticated && !isCachedResult) {
          const requiredCredits = calculateCredits(text, mode, includereasons);
          const deductSuccess = deductCredits(requiredCredits);
          
          if (deductSuccess) {
            // 크레딧 상태 업데이트
            const newCredits = getCurrentCredits();
            setGuestCredits(newCredits);
            console.log('[ChatApp] 크레딧 차감 완료:', {
              deducted: requiredCredits,
              remaining: newCredits
            });
          }
        }
        
        // 교정/교열 결과 메시지 추가 (새로운 타입 사용)
        const resultMessage = {
          id: Date.now() + 1,
          type: 'correction_result',
          data: data,
          mode: mode,
          originalText: text,
          isCached: isCachedResult,
          timestamp: new Date().toISOString()
        };
        console.log('[ChatApp] 결과 메시지 추가:', resultMessage);
        addMessage(chatId, resultMessage);
        
        // 로그인한 사용자의 경우 크레딧 갱신 (캐시된 결과가 아닌 경우에만)
        if (isAuthenticated && userApiKey && !isCachedResult) {
          try {
            console.log('[ChatApp] 크레딧 갱신 요청 시작');
            const creditResponse = await getCredits({ apikey: userApiKey });
            
            if (creditResponse.status === 200 && creditResponse.data) {
              const newCredits = creditResponse.data.remainingCredits;
              console.log('[ChatApp] 크레딧 갱신 성공:', newCredits);
              
              // otjUser 상태 업데이트
              setOtjUser(prevUser => ({
                ...prevUser,
                remainingCredits: newCredits
              }));
              
              // 크레딧이 0이 되면 크레딧 추가 기능 안내
              if (newCredits === 0) {
                const creditExhaustedMessage = {
                  id: Date.now() + 2,
                  type: 'credit_exhausted',
                  timestamp: new Date().toISOString()
                };
                addMessage(chatId, creditExhaustedMessage);
              }
            }
          } catch (error) {
            console.error('[ChatApp] 크레딧 갱신 실패:', error);
            // 크레딧 갱신 실패는 사용자에게 별도 알림 없이 조용히 처리
          }
        }
        
        // 채팅 제목을 입력 텍스트의 앞부분으로 업데이트 (한 번만)
        const currentChat = chats.find(c => c.id === chatId);
        if (currentChat && currentChat.title === '새로운 대화') {
          const newTitle = text.length > 20 ? text.substring(0, 20) + '...' : text;
          console.log('[ChatApp] 채팅 제목 업데이트:', newTitle);
          updateChatTitle(chatId, newTitle);
        }
      } else {
        console.log('[ChatApp] API 응답 오류:', response.status);
        throw new Error('API 응답 오류');
      }
    } catch (error) {
      console.error('[ChatApp] 텍스트 처리 오류 발생!');
      console.error('[ChatApp] 에러 타입:', typeof error);
      console.error('[ChatApp] 에러 메시지:', error.message);
      console.error('[ChatApp] 전체 에러 객체:', error);
      console.error('[ChatApp] 에러 스택:', error.stack);
      
      // 타이머 정리
      stopWaitingMessages();
      
      // 로딩 메시지와 대기 메시지 모두 제거
      setChats(prev => prev.map(chat => 
        chat.id === chatId 
          ? { ...chat, messages: chat.messages.filter(msg => msg.type !== 'loading' && msg.type !== 'waiting') }
          : chat
      ));

      // 오류 메시지 추가
      const errorMessage = {
        id: Date.now() + 1,
        type: 'error',
        content: `${mode === 'proofread' ? '교정' : '교열'} 처리 중 오류가 발생했습니다. 다시 시도해주세요.\n오류: ${error.message}`,
        timestamp: new Date().toISOString()
      };
      console.log('[ChatApp] 오류 메시지 추가:', errorMessage);
      addMessage(chatId, errorMessage);
    }
  };

  const currentChat = chats.find(chat => chat.id === currentChatId);

  // 인증 오류가 있는 경우만 표시 (로딩은 백그라운드에서 처리)

  if (authError) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-red-500 mb-4">
            <span className="material-icons text-4xl">error</span>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">인증 오류</h3>
          <p className="text-gray-600 mb-4">{authError}</p>
          <button
            onClick={handleUserAuthentication}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            다시 시도
          </button>
        </div>
      </div>
    );
  }

  // 사이드바 열림에 따라 본문 밀림
  const mainPushClass = isSidebarOpen ? 'ml-72 transition-all duration-200' : 'ml-0 transition-all duration-200';

  return (
    <div className="w-full h-screen flex bg-[#f8f9fa]">
      {/* 모바일 햄버거 버튼 임시 비노출 */}
      {false && (
        <button 
          className="md:hidden fixed top-4 left-4 z-50 bg-white text-gray-700 p-3 rounded-lg shadow-lg border border-gray-200" 
          onClick={() => setIsSidebarOpen(true)}
        >
          <span className="material-icons">menu</span>
        </button>
      )}
      
      {/* 사이드바 */}
      {/*
      <Sidebar
        open={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        chats={chats}
        currentChatId={currentChatId}
        onNewChat={createNewChat}
        onSelectChat={selectChat}
        onDeleteChat={deleteChat}
        onMenuSelect={handleMenuSelect}
        onOpenCreditPanel={openCreditPanel}
        currentView={currentView}
      />
      */}
      
      {/* 메인 영역 */}
      <div className={`flex-1 flex flex-col h-full ${mainPushClass}`}> 
        {currentView === 'chat' ? (
          <>
            {/* 메시지 출력 영역 */}
            <div className="flex-1 overflow-y-auto px-2">
              <ChatArea
                chat={currentChat}
                onToggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
                onAddMessage={addMessage}
                onUpdateChatTitle={updateChatTitle}
                onTextProcessing={handleTextProcessing}
                onOpenCreditPanel={openCreditPanel}
                isSidebarOpen={isSidebarOpen}
                otjUser={otjUser}
              />
            </div>
            {/* 입력창 + 카드 선택 영역 */}
            <div className="bg-grey-300 border-t px-2 py-1 sticky bottom-0 z-10">
              <MessageInput
                onSend={handleTextProcessing}
                remainingCredits={otjUser?.remainingCredits}
                guestCredits={guestCredits}
                isAuthenticated={isAuthenticated}
              />
            </div>
          </>
        ) : (
          /* 파일 전송 화면 */
          <FilePanel
            userEmail={otjUser?.email || ''}
            onEmailChange={(email) => {
              // 필요시 otjUser 업데이트
              if (otjUser) {
                setOtjUser(prev => ({ ...prev, email }));
              }
            }}
            userApiKey={otjUser?.primaryKey}
            provider={otjUser?.provider}
            provideruserid={otjUser?.provideruserid}
          />
        )}
      </div>
      
      {/* 크레딧 추가 패널 */}
      <CreditPanel
        isOpen={isCreditPanelOpen}
        onClose={closeCreditPanel}
        currentCredits={otjUser?.remainingCredits || guestCredits}
        otjUser={otjUser}
        onCreditAdded={handleCreditAdded}
      />
    </div>
  );
};

export default ChatApp; 